import {
  Button,
  Chip,
  Divider,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { medium } from '@styles/fontWeights';
import React, { FC, useState } from 'react';
import parse from 'html-react-parser';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { ApplicationQuestion } from '@features/applicationQuestions';
import DeleteTemplateQuestionModal from './DeleteTemplateQuestionModal';
import AddQuestionsModal from './AddQuestionsModal';
import AssignedQuestionsTags from './AssignedQuestionsTags';
import { EnterpriseTemplate } from '../types';
import { getCookie } from '@utils/getCookie';
import ConfirmationModal from './ConfirmationModal';

type TemplateQuestionsCardProps = {
  applicationQuestion: ApplicationQuestion;
  templateData: EnterpriseTemplate;
  tagsList: any;
};
const TemplateQuestionsCard: FC<TemplateQuestionsCardProps> = ({
  applicationQuestion,
  templateData,
  tagsList,
}) => {
  const dontShowWarningPopup = getCookie('dontShowWarningPopup');
  const isAppliedChanges = templateData.apply_changes && !dontShowWarningPopup;
  const [selectedQuestion, setSelectedQuestion] =
    useState<ApplicationQuestion | null>(null);
  const [pendingUpdate, setPendingUpdate] =
    useState<ApplicationQuestion | null>(null);
  const [checkDelete, setCheckDelete] = useState(false);
  const [isApplicationQuestionModalOpen, setIsApplicationQuestionModalOpen] =
    useState(false);
  const [
    isDeleteApplicationQuestionModalOpen,
    setIsDeleteApplicationQuestionModalOpen,
  ] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const relevantTags = tagsList?.objects?.filter((tag: { id: number }) =>
    ((applicationQuestion.yes_case_tags ?? []) as number[])
      .concat((applicationQuestion.no_case_tags ?? []) as number[])
      .includes(tag.id),
  );
  const handleCancel = () => {
    setCheckDelete(false);
    setPendingUpdate(null);
    setIsConfirmOpen(false);
  };
  const handleConfirm = () => {
    if (pendingUpdate && !checkDelete) {
      setSelectedQuestion(pendingUpdate);
      setIsApplicationQuestionModalOpen(true);
    }
    if (pendingUpdate && checkDelete) {
      setSelectedQuestion(pendingUpdate);
      setIsDeleteApplicationQuestionModalOpen(true);
    }
    setIsConfirmOpen(false);
    setPendingUpdate(null);
    setCheckDelete(false);
  };
  return (
    <>
      <Stack
        direction={{ sm: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems="center"
      >
        <Stack spacing={1}>
          <Stack spacing={1}>
            <Stack
              spacing={3}
              direction="row"
              alignItems="center"
              sx={{ mb: 1 }}
            >
              <Typography variant="h5">{applicationQuestion.label}</Typography>
              <AssignedQuestionsTags relevantTags={relevantTags} />
              {applicationQuestion.disqualify && (
                <Stack
                  spacing={1}
                  direction="row"
                  alignItems="center"
                  sx={{ mb: 1 }}
                >
                  <Chip color="success" label="Pre-Qualifier" size="small" />
                  <Tooltip
                    title={`Applicants who answer ${applicationQuestion.preferred_answer === 'No' ? 'YES' : 'NO'} to this question will be automatically declined with your default decline message.`}
                    placement="top"
                    arrow
                  >
                    <HelpOutlineIcon color="secondary" fontSize="small" />
                  </Tooltip>
                </Stack>
              )}
            </Stack>
            <Stack spacing={1} direction="row" alignItems="center">
              <Typography color="text.secondary" variant="subtitle2">
                {parse(applicationQuestion.question)}
              </Typography>
            </Stack>
          </Stack>
          <Stack spacing={2} flexWrap="wrap" direction="row" useFlexGap>
            {applicationQuestion.all_positions_selected ? (
              <Chip label="All Positions" sx={{ fontWeight: medium }} />
            ) : (
              applicationQuestion?.job_postings.map(({ s_title, id }) => (
                <Chip label={s_title} key={id} sx={{ fontWeight: medium }} />
              ))
            )}
          </Stack>
        </Stack>
        <Stack spacing={2}>
          <Button
            onClick={() => {
              if (isAppliedChanges) {
                setPendingUpdate(applicationQuestion);
                setIsConfirmOpen(true);
              } else {
                setSelectedQuestion(applicationQuestion);
                setIsApplicationQuestionModalOpen(true);
              }
            }}
            size="small"
            variant="contained"
          >
            Edit
          </Button>
          <Button
            onClick={() => {
              if (isAppliedChanges) {
                setPendingUpdate(applicationQuestion);
                setCheckDelete(true);
                setIsConfirmOpen(true);
              } else {
                setSelectedQuestion(applicationQuestion);
                setIsDeleteApplicationQuestionModalOpen(true);
              }
            }}
            size="small"
            variant="contained"
            color="error"
          >
            Delete
          </Button>
        </Stack>
      </Stack>
      <Divider sx={{ marginTop: '7px' }} />

      <DeleteTemplateQuestionModal
        isOpen={isDeleteApplicationQuestionModalOpen}
        handleClose={() => {
          setIsDeleteApplicationQuestionModalOpen(false);
          setSelectedQuestion(null);
        }}
        applicationQuestion={selectedQuestion}
        templateId={templateData?.id}
      />

      <AddQuestionsModal
        isOpen={isApplicationQuestionModalOpen}
        applicationQuestion={selectedQuestion}
        templateId={templateData?.id}
        jobPostingsData={templateData?.job_listing}
        tagsList={tagsList}
        handleClose={() => {
          setSelectedQuestion(null);
          setIsApplicationQuestionModalOpen(false);
        }}
      />
      <ConfirmationModal
        open={isConfirmOpen}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </>
  );
};

export default TemplateQuestionsCard;
